import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, CheckCircle } from 'lucide-react';

const AnimatedDropdown = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  disabled = false,
  className = '',
  optionClassName = '',
  renderOption = null,
  renderValue = null,
  showCheckmark = true,
  icon: Icon = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownMenuRef.current) {
      const updatePosition = () => {
        if (!buttonRef.current || !dropdownMenuRef.current) return;
        
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownElement = dropdownMenuRef.current;
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 240; // max-h-60 = 240px (approximate)
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Check if there's enough space below, if not, open upward
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          // Open upward
          dropdownElement.style.position = 'fixed';
          dropdownElement.style.bottom = `${viewportHeight - buttonRect.top + 8}px`;
          dropdownElement.style.top = 'auto';
          dropdownElement.style.left = `${buttonRect.left}px`;
          dropdownElement.style.width = `${buttonRect.width}px`;
          dropdownElement.style.maxHeight = `${Math.min(dropdownHeight, spaceAbove - 16)}px`;
        } else {
          // Open downward (default)
          dropdownElement.style.position = 'fixed';
          dropdownElement.style.top = `${buttonRect.bottom + 8}px`;
          dropdownElement.style.bottom = 'auto';
          dropdownElement.style.left = `${buttonRect.left}px`;
          dropdownElement.style.width = `${buttonRect.width}px`;
          dropdownElement.style.maxHeight = `${Math.min(dropdownHeight, spaceBelow - 16)}px`;
        }
      };
      
      updatePosition();
      
      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => {
    const optValue = typeof opt === 'object' ? opt.value : opt;
    return optValue === value;
  });

  const getDisplayValue = () => {
    if (renderValue && selectedOption) {
      return renderValue(selectedOption);
    }
    if (selectedOption) {
      return typeof selectedOption === 'object' ? selectedOption.label : selectedOption;
    }
    return placeholder;
  };

  const handleSelect = (optionValue) => {
    if (optionValue !== undefined && optionValue !== null) {
      onChange(optionValue);
    }
    setIsOpen(false);
  };

  return (
    <>
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full bg-background border ${
            isOpen 
              ? 'border-primary shadow-lg shadow-primary/20' 
              : 'border-primary/50 hover:border-primary hover:shadow-lg hover:shadow-primary/10'
          } rounded-lg px-4 py-3 text-left text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 flex items-center justify-between group ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <span className={`flex items-center gap-2 flex-1 min-w-0 ${value ? 'text-foreground' : 'text-muted-foreground'}`}>
            {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
            <span className="truncate">{getDisplayValue()}</span>
          </span>
          <ChevronDown 
            className={`w-4 h-4 text-primary transition-transform duration-300 flex-shrink-0 ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </button>
      </div>
      
      {/* Dropdown Menu using Portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownMenuRef}
          className="fixed z-[99999] bg-card/95 backdrop-blur-md border border-border/60 rounded-lg shadow-elegant overflow-hidden dropdown-open"
        >
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((option, index) => {
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              const isSelected = optionValue === value;
              const isDisabled = typeof option === 'object' && option.disabled;

              return (
                <button
                  key={optionValue || index}
                  type="button"
                  onClick={() => !isDisabled && handleSelect(optionValue)}
                  disabled={isDisabled}
                  className={`w-full px-4 py-3 text-left text-sm transition-all duration-200 hover:bg-primary/10 hover:text-primary flex items-center justify-between group dropdown-item ${optionClassName} ${
                    isSelected 
                      ? 'bg-primary/10 text-primary' 
                      : isDisabled
                      ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                      : 'text-foreground'
                  }`}
                  style={{
                    animationDelay: `${index * 0.02}s`
                  }}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {showCheckmark && isSelected && <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />}
                    {renderOption ? renderOption(option) : <span className="truncate">{optionLabel}</span>}
                  </div>
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse ml-2 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AnimatedDropdown;

